import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router';
import { PlacementType, } from '@amzn/handshake-painter-typescript-client/v1';
import { HandshakePainterServiceApiV1 } from 'src/api/handshakeApi';
import { useDefaultApiParams } from './useDefaultApiParams';
import { resolveStorefront, parseCategories } from 'src/pages/storefrontPage/utils';
import { useAnalyticsContext } from 'src/common/metrics';
// Shared query config for category product fetches
const getCategoryQueryConfig = (strategy, category, getDefaultParams) => ({
    queryKey: ['getStorefrontProducts', strategy, category],
    queryFn: () => HandshakePainterServiceApiV1.getStorefrontProducts(Object.assign({ strategy,
        category }, getDefaultParams())),
    staleTime: Infinity,
});
/**
 * Resolves storefront metadata and fetches products.
 * - REGULAR storefronts → product widget API (ASIN-based)
 * - DEALS storefronts   → storefront API (strategy + category)
 */
export const useStorefront = (options) => {
    var _a;
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const { getDefaultParams } = useDefaultApiParams();
    const rumClient = useAnalyticsContext();
    const isEnabled = (_a = options === null || options === void 0 ? void 0 : options.enabled) !== null && _a !== void 0 ? _a : true;
    const { storefrontId, isValid, storeMeta } = resolveStorefront(id);
    const { storefrontCategories, invalidCategories } = parseCategories(storeMeta, searchParams);
    const firstCategory = storefrontCategories[0];
    const canFetch = storeMeta.type === 'DEALS' ? !!firstCategory : storeMeta.products.length > 0;
    // Analytics logging — fire only once on initial page load
    useEffect(() => {
        if (id && !isValid) {
            rumClient.recordEvent('UNKNOWN_STOREFRONT_ID', { requestedId: id, url: window.location.href });
        }
        if (invalidCategories.length > 0) {
            rumClient.recordEvent('INVALID_STOREFRONT_CATEGORIES', {
                storefrontId,
                invalidCategories: invalidCategories.join(','),
                url: window.location.href,
            });
        }
        if (storeMeta.type === 'DEALS' && storefrontCategories.length === 0) {
            rumClient.recordEvent('EMPTY_STOREFRONT_CATEGORIES', { storefrontId, url: window.location.href });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps  -- run only once on mount
    }, []);
    const result = useQuery(Object.assign(Object.assign({}, (storeMeta.type === 'DEALS'
        ? getCategoryQueryConfig(storeMeta.strategy, firstCategory, getDefaultParams)
        : {
            queryKey: ['getProductWidget', storeMeta.products],
            queryFn: () => HandshakePainterServiceApiV1.getProductWidget(Object.assign({ asins: storeMeta.products, placement: PlacementType.CustomersAlsoBought }, getDefaultParams())),
            staleTime: Infinity,
        })), { enabled: isEnabled && canFetch }));
    return Object.assign(Object.assign({}, result), { storefrontId, storeMeta, storefrontCategories });
};
/**
 * Prefetches all non-first category tabs for DEALS storefronts.
 * Waits until enabled (first tab data loaded) to ensure app init is ready.
 * Uses the same query key as useCategoryProducts so tab switches are instant from cache.
 */
export const usePrefetchCategories = (storefrontId, categories, enabled) => {
    const queryClient = useQueryClient();
    const { getDefaultParams } = useDefaultApiParams();
    const { storeMeta } = resolveStorefront(storefrontId);
    useEffect(() => {
        if (!enabled || storeMeta.type !== 'DEALS' || categories.length <= 1)
            return;
        categories.slice(1).forEach((category) => {
            queryClient.prefetchQuery(getCategoryQueryConfig(storeMeta.strategy, category, getDefaultParams));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- prefetch once when first tab data is ready
    }, [enabled]);
};
/**
 * Reads from React Query cache (populated by usePrefetchCategories) for instant tab switches.
 * Falls back to a network fetch if the cache is empty.
 */
export const useCategoryProducts = (storefrontId, category) => {
    const { getDefaultParams } = useDefaultApiParams();
    const { storeMeta } = resolveStorefront(storefrontId);
    const strategy = storeMeta.type === 'DEALS' ? storeMeta.strategy : undefined;
    return useQuery(Object.assign(Object.assign({}, getCategoryQueryConfig(strategy, category, getDefaultParams)), { enabled: !!strategy && !!category }));
};
